// src/components/continents/ContinentNamesModal.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import apiClient from '../../services/ApiClient';
import { readApiError, unwrap } from '../../services/catalogApi';
import styles from './ContinentNamesModal.module.css';

// Kullanım bu üçünde yoğun; gerisi "Diğer diller" altında toplanır.
const PRIMARY_LANGUAGES = ['tr', 'en', 'de'];

const LANGUAGE_LABELS = {
    tr: 'Türkçe',
    en: 'İngilizce',
    de: 'Almanca',
    fr: 'Fransızca',
    es: 'İspanyolca',
    ar: 'Arapça',
    pt: 'Portekizce',
    ru: 'Rusça',
    ur: 'Urduca',
    it: 'İtalyanca',
    sr: 'Sırpça',
    id: 'Endonezce',
    mk: 'Makedonca',
    hu: 'Macarca',
    ja: 'Japonca',
    zh: 'Çince',
    th: 'Tayca',
};

// Sağdan sola yazılan diller; giriş alanı yönünü buna göre alır.
const RTL_LANGUAGES = ['ar', 'ur'];

// Sunucu yalnızca biçimi doğruluyor (örn. tr, en, pt-BR); listeye bağlı değil.
const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}(-[A-Z]{2})?$/;

const languageLabel = (code) => LANGUAGE_LABELS[code] || code;

/**
 * Bir kıtanın 17+ dildeki adlarını düzenler.
 *
 * Uç kısmi güncelleme yapıyor: yalnızca değişen diller gönderilir. Bir dili
 * kaldırmak desteklenmiyor, o yüzden ad boş bırakılamaz.
 */
const ContinentNamesModal = ({ isOpen, continent, onClose, onSaved }) => {
    const [saved, setSaved] = useState({});
    const [form, setForm] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [newCode, setNewCode] = useState('');
    const [newCodeError, setNewCodeError] = useState('');

    const continentId = continent?.id;

    const loadTranslations = useCallback(async (id) => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await apiClient.get(`/continents/${id}/translations`);
            const data = unwrap(response.data) || {};
            setSaved(data);
            setForm(data);
        } catch (err) {
            console.error('Kıta çevirileri yüklenirken hata:', err);
            setLoadError(readApiError(err, 'Kıta adları yüklenemedi.').message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Modal her açılışta sunucudaki güncel adlarla başlar.
    useEffect(() => {
        if (!isOpen || continentId == null) return;
        setSaved({});
        setForm({});
        setShowErrors(false);
        setNewCode('');
        setNewCodeError('');
        loadTranslations(continentId);
    }, [isOpen, continentId, loadTranslations]);

    const codes = useMemo(() => Object.keys(form), [form]);
    const primaryCodes = PRIMARY_LANGUAGES.filter((code) => codes.includes(code));
    const otherCodes = codes
        .filter((code) => !PRIMARY_LANGUAGES.includes(code))
        .sort((a, b) => languageLabel(a).localeCompare(languageLabel(b), 'tr'));

    const errors = useMemo(() => {
        const next = {};
        codes.forEach((code) => {
            if (!(form[code] || '').trim()) next[code] = 'Ad boş bırakılamaz.';
        });
        return next;
    }, [form, codes]);

    // Yalnızca değişen (ya da yeni eklenen) diller.
    const patch = useMemo(() => {
        const next = {};
        codes.forEach((code) => {
            const value = (form[code] || '').trim();
            if (value && value !== saved[code]) next[code] = value;
        });
        return next;
    }, [form, saved, codes]);

    const changedCount = Object.keys(patch).length;
    const hasErrors = Object.keys(errors).length > 0;
    // Hatalı bir alan "Diğer diller" kapalıyken gözden kaçmasın.
    const otherHasError = showErrors && otherCodes.some((code) => errors[code]);

    const setField = (code, value) => {
        setForm((prev) => ({ ...prev, [code]: value }));
    };

    const handleAddLanguage = () => {
        const code = newCode.trim();
        if (!LANGUAGE_CODE_PATTERN.test(code)) {
            setNewCodeError('Geçersiz dil kodu. Örnek: tr, en, pt-BR');
            return;
        }
        if (code in form) {
            setNewCodeError(`${code} zaten listede.`);
            return;
        }
        setForm((prev) => ({ ...prev, [code]: '' }));
        setNewCode('');
        setNewCodeError('');
    };

    const handleRemoveNew = (code) => {
        setForm((prev) => {
            const next = { ...prev };
            delete next[code];
            return next;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (hasErrors) {
            setShowErrors(true);
            return;
        }
        if (changedCount === 0) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            await apiClient.patch(`/continents/${continentId}`, { translations: patch });
            toast.success(`${continent.name} adları güncellendi (${changedCount} dil).`);
            onSaved?.();
        } catch (err) {
            console.error('Kıta adları kaydedilirken hata:', err);
            toast.error(readApiError(err, 'Kıta adları kaydedilemedi.').message);
        } finally {
            setIsSaving(false);
        }
    };

    const renderField = (code) => {
        const isNew = !(code in saved);
        const isChanged = code in patch;
        const error = showErrors && errors[code];
        return (
            <div key={code} className={styles.field}>
                <label className={styles.label} htmlFor={`continent-name-${code}`}>
                    <span>{languageLabel(code)}</span>
                    <span className={styles.code}>{code}</span>
                    {isChanged && <span className={styles.changed}>{isNew ? 'yeni' : 'değişti'}</span>}
                    {isNew && (
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => handleRemoveNew(code)}
                            disabled={isSaving}
                        >
                            Vazgeç
                        </button>
                    )}
                </label>
                <input
                    id={`continent-name-${code}`}
                    type="text"
                    dir={RTL_LANGUAGES.includes(code) ? 'rtl' : 'auto'}
                    className={`${styles.input} ${error ? styles.inputError : ''}`}
                    value={form[code] ?? ''}
                    onChange={(e) => setField(code, e.target.value)}
                    disabled={isSaving}
                    autoComplete="off"
                />
                {error && <span className={styles.errorText}>{errors[code]}</span>}
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={isSaving ? () => {} : onClose}
            title={continent ? `${continent.name} — Adlar` : 'Kıta adları'}
        >
            {isLoading && (
                <div className={styles.loading}>
                    <LoadingSpinner />
                </div>
            )}

            {!isLoading && loadError && (
                <div className={styles.loadError}>
                    <span>{loadError}</span>
                    <button type="button" className={styles.linkButton} onClick={() => loadTranslations(continentId)}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {!isLoading && !loadError && (
                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                    <div className={styles.grid}>{primaryCodes.map(renderField)}</div>

                    {otherCodes.length > 0 && (
                        <details className={styles.more} open={otherHasError || undefined}>
                            <summary className={styles.moreSummary}>
                                Diğer diller <span className={styles.moreCount}>{otherCodes.length}</span>
                            </summary>
                            <div className={`${styles.grid} ${styles.moreBody}`}>{otherCodes.map(renderField)}</div>
                        </details>
                    )}

                    <div className={styles.addRow}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="continent-new-language">Dil ekle</label>
                            <div className={styles.addControls}>
                                <input
                                    id="continent-new-language"
                                    type="text"
                                    className={`${styles.input} ${styles.codeInput} ${newCodeError ? styles.inputError : ''}`}
                                    placeholder="pt-BR"
                                    value={newCode}
                                    onChange={(e) => {
                                        setNewCode(e.target.value);
                                        setNewCodeError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddLanguage();
                                        }
                                    }}
                                    disabled={isSaving}
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                <button
                                    type="button"
                                    className={styles.ghostButton}
                                    onClick={handleAddLanguage}
                                    disabled={isSaving || !newCode.trim()}
                                >
                                    Ekle
                                </button>
                            </div>
                            {newCodeError ? (
                                <span className={styles.errorText}>{newCodeError}</span>
                            ) : (
                                <span className={styles.hint}>
                                    Listede olmayan bir dil için. Eklenen dil kaydedildikten sonra kaldırılamaz.
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <span className={styles.footerNote}>
                            {changedCount > 0
                                ? `${changedCount} dil kaydedilecek; diğerlerine dokunulmaz.`
                                : 'Değişiklik yok.'}
                        </span>
                        <div className={styles.actions}>
                            <button type="button" className={styles.ghostButton} onClick={onClose} disabled={isSaving}>
                                Vazgeç
                            </button>
                            <button type="submit" className={styles.saveButton} disabled={isSaving || changedCount === 0}>
                                {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </Modal>
    );
};

export default ContinentNamesModal;
